

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useModal } from '@/contexts/ModalContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import ConfirmModal from '@/components/modals/ConfirmModal'
import ScriptEditorModal from '@/components/modals/ScriptEditorModal'
import { IoMdPause, IoMdPlay, IoMdSkipBackward, IoMdSkipForward } from 'react-icons/io'
import { FiZoomIn, FiZoomOut, FiPlus, FiImage, FiChevronDown, FiAlertTriangle } from 'react-icons/fi'
import { LuAudioLines } from 'react-icons/lu'
import { TbMagnet } from 'react-icons/tb'
import Button from '@/components/ui/Button'
import { Button as ShadButton } from '@/components/ui/shadcn/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import TimelineAudioEngine from '@/lib/TimelineAudioEngine'

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
dayjs.extend(duration)

const WPM = 140
const DEFAULT_ELEMENT_DURATION = 5000

export const SCRIPT_DRAG_TYPE = 'application/x-kagami-storyboard-script'

// Utility: compute script element duration in ms
// When an audioEngine is provided, prefer the actual decoded buffer duration (most accurate).
// Falls back to stored audio_duration metadata, then to a WPM-based estimate.
function getScriptDuration(element, audioEngine = null, wpm = WPM) {
    if (audioEngine && element._id) {
        const bufferDuration = audioEngine.getBufferDurationMs(element._id)
        if (bufferDuration !== null) return bufferDuration
    }
    if (element.audio_duration) return element.audio_duration
    const wordCount = element.speech?.split(' ').filter(w => w.trim()).length || 0
    return Math.max((wordCount / (wpm / 60)) * 1000, 2000)
}

// Utility: format ms to mm:ss
function formatTime(ms) {
    return dayjs.duration(ms, 'milliseconds').format('mm:ss')
}

export default function StoryboardTimeline({
    storyboard,
    updateStoryboard,
    selectedElementIndex,
    setSelectedElementIndex,
    triggerDeselectItems,
    segment,
    onActiveScriptChange,
}) {
    const { openModal } = useModal()
    const { fetchEpisode } = useEpisode()
    const { runTaskAndPoll, taskStatus } = useKagamiBridge()

    // --- State ---
    const [selectedElementIndices, setSelectedElementIndices] = useState(new Set())
    const [scale, setScale] = useState(50)
    const [playheadTime, setPlayheadTime] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [sbDrag, setSbDrag] = useState(null)
    const [sbResize, setSbResize] = useState(null)
    const [snapEnabled, setSnapEnabled] = useState(true)
    const [generatingVoices, setGeneratingVoices] = useState(false)
    const [voiceGenProgress, setVoiceGenProgress] = useState('')
    const [bufferLoadCount, setBufferLoadCount] = useState(0)

    // Sync local selection when parent changes selectedElementIndex externally
    useEffect(() => {
        if (selectedElementIndex !== null && !selectedElementIndices.has(selectedElementIndex)) {
            setSelectedElementIndices(new Set([selectedElementIndex]))
        } else if (selectedElementIndex === null && selectedElementIndices.size > 0) {
            setSelectedElementIndices(new Set())
        }
    }, [selectedElementIndex])

    // --- Refs ---
    const timelineRef = useRef(null)
    const scrubRef = useRef(null)
    const timelineScrollRef = useRef(null)
    const playbackRef = useRef(false)
    const playbackIntervalRef = useRef(null)
    const playbackLastTickRef = useRef(null)
    const audioEngineRef = useRef(null)
    const pointerOverTimelineRef = useRef(false)

    // Lazy-init the audio engine
    const getAudioEngine = useCallback(() => {
        if (!audioEngineRef.current) {
            audioEngineRef.current = new TimelineAudioEngine()
        }
        return audioEngineRef.current
    }, [])

    // --- Derived data ---
    const scripts = useMemo(() => {
        if (!segment) return []
        const all = []
        segment.scenes?.forEach(scene => {
            scene.script?.forEach(element => {
                if (element.character) {
                    all.push({ ...element, _sceneId: scene._id, _sceneName: scene.name })
                }
            })
        })
        return all
    }, [segment])

    const uniqueCharacters = useMemo(() => {
        const seen = new Map()
        scripts.forEach(s => {
            if (s.character && !seen.has(s.character._id)) {
                seen.set(s.character._id, s.character)
            }
        })
        return Array.from(seen.values())
    }, [scripts])

    const totalScriptDuration = useMemo(() => {
        const engine = audioEngineRef.current
        return scripts.reduce((acc, s) => acc + getScriptDuration(s, engine), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scripts, bufferLoadCount])

    const scriptTimings = useMemo(() => {
        const engine = audioEngineRef.current
        let t = 0
        return scripts.map(s => {
            const start = t
            const dur = getScriptDuration(s, engine)
            t += dur
            return { start, duration: dur }
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scripts, bufferLoadCount])

    const totalDuration = useMemo(() => {
        let sbEnd = 0
        if (storyboard?.elements) {
            storyboard.elements.forEach(el => {
                const end = el.startTime + el.duration
                if (end > sbEnd) sbEnd = end
            })
        }
        return Math.max(totalScriptDuration, sbEnd) || 5000
    }, [totalScriptDuration, storyboard])

    // --- Active script element under playhead (works for both playback and scrubbing) ---
    const activeScriptIndex = useMemo(() => {
        for (let i = 0; i < scriptTimings.length; i++) {
            const t = scriptTimings[i]
            if (playheadTime >= t.start && playheadTime < t.start + t.duration) {
                return i
            }
        }
        return null
    }, [playheadTime, scriptTimings])

    useEffect(() => {
        const id = activeScriptIndex !== null ? scripts[activeScriptIndex]?._id : null
        onActiveScriptChange?.(id)
    }, [activeScriptIndex, scripts, onActiveScriptChange])

    // --- Timeline width helpers ---
    const msToPixels = useCallback((ms) => (ms / 1000) * scale, [scale])
    const pixelsToMs = useCallback((px) => (px / scale) * 1000, [scale])
    const timelineWidth = useMemo(() => msToPixels(totalDuration), [msToPixels, totalDuration])

    const minScale = useMemo(() => {
        const container = timelineScrollRef.current
        if (!container || totalDuration <= 0) return 5
        return Math.max(5, Math.ceil((container.clientWidth / (totalDuration / 1000))))
    }, [totalDuration, timelineWidth])

    useEffect(() => {
        if (scale < minScale) setScale(minScale)
    }, [minScale])

    const zoomTimeline = useCallback((newScale) => {
        const clamped = Math.max(minScale, Math.min(120, newScale))
        const container = timelineScrollRef.current
        if (!container) { setScale(clamped); return }

        const playheadPxBefore = (playheadTime / 1000) * scale
        const playheadViewportOffset = playheadPxBefore - container.scrollLeft

        setScale(clamped)

        requestAnimationFrame(() => {
            const playheadPxAfter = (playheadTime / 1000) * clamped
            container.scrollLeft = playheadPxAfter - playheadViewportOffset
        })
    }, [playheadTime, scale, minScale])

    // --- Sync scrub input width with timeline ---
    useEffect(() => {
        if (!timelineRef.current || !scrubRef.current) return
        const syncWidth = () => {
            if (!timelineRef.current) return
            const w = timelineRef.current.scrollWidth
            if (scrubRef.current) scrubRef.current.style.width = `${w}px`
        }
        syncWidth()
        const ro = new ResizeObserver(syncWidth)
        ro.observe(timelineRef.current)
        return () => ro.disconnect()
    }, [timelineWidth])

    const scrubPosition = totalDuration > 0 ? playheadTime / totalDuration : 0

    // --- Determine which storyboard element is active at a given time ---
    const getActiveElementAtTime = useCallback((time) => {
        if (!storyboard?.elements) return null
        for (let i = 0; i < storyboard.elements.length; i++) {
            const el = storyboard.elements[i]
            if (time >= el.startTime && time < el.startTime + el.duration) {
                return i
            }
        }
        return null
    }, [storyboard])

    // ==============================
    // STORYBOARD ELEMENT CRUD
    // ==============================
    const addStoryboardElement = () => {
        updateStoryboard(prev => {
            const elements = [...(prev?.elements || [])]
            let startTime = playheadTime
            if (elements.length > 0) {
                const last = elements[elements.length - 1]
                startTime = Math.max(playheadTime, last.startTime + last.duration)
            }
            elements.push({ name: `Panel ${elements.length + 1}`, startTime, duration: DEFAULT_ELEMENT_DURATION, items: [] })
            return { ...prev, elements }
        })
        const newIdx = storyboard?.elements?.length || 0
        setSelectedElementIndex(newIdx)
        setSelectedElementIndices(new Set([newIdx]))
        triggerDeselectItems()
    }

    const deleteSelectedElements = async () => {
        if (selectedElementIndices.size === 0) return
        const count = selectedElementIndices.size
        const confirmed = await openModal(
            <ConfirmModal
                title='Remove Panel'
                message={count === 1
                    ? 'Are you sure you want to remove this storyboard panel?'
                    : `Are you sure you want to remove ${count} storyboard panels?`}
            />
        )
        if (!confirmed) return
        const indicesToRemove = new Set(selectedElementIndices)
        updateStoryboard(prev => {
            const elements = prev.elements.filter((_, i) => !indicesToRemove.has(i))
            return { ...prev, elements }
        })
        setSelectedElementIndices(new Set())
        setSelectedElementIndex(null)
        triggerDeselectItems()
    }

    // ==============================
    // TIMELINE DRAG & RESIZE
    // ==============================
    const handleSbDragStart = (e, index) => {
        e.stopPropagation()
        e.preventDefault()
        if (isPlaying) pausePlayback()
        const isMultiKey = e.ctrlKey || e.metaKey

        let newSelection
        if (isMultiKey) {
            newSelection = new Set(selectedElementIndices)
            if (newSelection.has(index)) {
                newSelection.delete(index)
            } else {
                newSelection.add(index)
            }
        } else if (!selectedElementIndices.has(index)) {
            newSelection = new Set([index])
        } else {
            newSelection = selectedElementIndices
        }
        setSelectedElementIndices(newSelection)
        setSelectedElementIndex(index)
        triggerDeselectItems()

        if (newSelection.size > 0 && newSelection.has(index)) {
            const startTimes = {}
            for (const idx of newSelection) {
                startTimes[idx] = storyboard.elements[idx].startTime
            }
            setSbDrag({
                index,
                indices: newSelection,
                startX: e.clientX,
                startTimes,
            })
        }
    }

    const handleSbResizeStart = (e, index, edge) => {
        e.stopPropagation()
        e.preventDefault()
        const el = storyboard.elements[index]
        setSbResize({
            index,
            edge,
            startX: e.clientX,
            startTime: el.startTime,
            startDuration: el.duration
        })
    }

    // Snap helpers
    const getSnapEdges = useCallback((excludeIndex) => {
        const SNAP_THRESHOLD_MS = pixelsToMs(8)
        const edges = []
        storyboard?.elements?.forEach((el, i) => {
            if (i === excludeIndex) return
            edges.push(el.startTime)
            edges.push(el.startTime + el.duration)
        })
        scriptTimings.forEach(t => {
            edges.push(t.start)
            edges.push(t.start + t.duration)
        })
        return { edges, threshold: SNAP_THRESHOLD_MS }
    }, [storyboard, scriptTimings, pixelsToMs])

    const snapTime = useCallback((time, excludeIndex) => {
        if (!snapEnabled) return time
        const { edges, threshold } = getSnapEdges(excludeIndex)
        let closest = time
        let minDist = threshold
        for (const edge of edges) {
            const dist = Math.abs(time - edge)
            if (dist < minDist) {
                minDist = dist
                closest = edge
            }
        }
        return closest
    }, [snapEnabled, getSnapEdges])

    useEffect(() => {
        if (!sbDrag && !sbResize) return

        const handleMove = (e) => {
            if (sbDrag) {
                const dx = e.clientX - sbDrag.startX
                const dtMs = pixelsToMs(dx)
                const dragIndices = sbDrag.indices || new Set([sbDrag.index])

                const anchorStartTime = sbDrag.startTimes?.[sbDrag.index] ?? sbDrag.startTime ?? 0
                const rawStart = Math.max(0, anchorStartTime + dtMs)
                const el = storyboard.elements[sbDrag.index]
                const dur = el.duration

                const SNAP_THRESHOLD_MS = pixelsToMs(8)
                const snapEdges = []
                storyboard?.elements?.forEach((sEl, i) => {
                    if (dragIndices.has(i)) return
                    snapEdges.push(sEl.startTime)
                    snapEdges.push(sEl.startTime + sEl.duration)
                })
                scriptTimings.forEach(t => {
                    snapEdges.push(t.start)
                    snapEdges.push(t.start + t.duration)
                })
                const snapVal = (time) => {
                    if (!snapEnabled) return time
                    let closest = time, minDist = SNAP_THRESHOLD_MS
                    for (const edge of snapEdges) {
                        const d = Math.abs(time - edge)
                        if (d < minDist) { minDist = d; closest = edge }
                    }
                    return closest
                }

                const snappedLeft = snapVal(rawStart)
                const snappedRight = snapVal(rawStart + dur) - dur
                const leftDidSnap = snappedLeft !== rawStart
                const rightDidSnap = snappedRight !== rawStart
                let newStart = rawStart
                if (leftDidSnap && rightDidSnap) {
                    newStart = Math.abs(snappedLeft - rawStart) <= Math.abs(snappedRight - rawStart) ? snappedLeft : snappedRight
                } else if (leftDidSnap) {
                    newStart = snappedLeft
                } else if (rightDidSnap) {
                    newStart = snappedRight
                }
                newStart = Math.max(0, newStart)
                const snapDt = newStart - anchorStartTime

                updateStoryboard(prev => {
                    const elements = [...prev.elements]
                    for (const idx of dragIndices) {
                        const origStart = sbDrag.startTimes?.[idx] ?? elements[idx].startTime
                        elements[idx] = { ...elements[idx], startTime: Math.max(0, origStart + snapDt) }
                    }
                    return { ...prev, elements }
                })
            }
            if (sbResize) {
                const dx = e.clientX - sbResize.startX
                const dtMs = pixelsToMs(dx)
                if (sbResize.edge === 'right') {
                    const rawEnd = sbResize.startTime + sbResize.startDuration + dtMs
                    const snappedEnd = snapTime(rawEnd, sbResize.index)
                    const newDur = Math.max(250, snappedEnd - sbResize.startTime)
                    updateStoryboard(prev => {
                        const elements = [...prev.elements]
                        elements[sbResize.index] = { ...elements[sbResize.index], duration: newDur }
                        return { ...prev, elements }
                    })
                } else {
                    const rawStart = sbResize.startTime + dtMs
                    const snappedStart = snapTime(rawStart, sbResize.index)
                    const newStart = Math.max(0, snappedStart)
                    const newDur = Math.max(250, sbResize.startDuration - (newStart - sbResize.startTime))
                    updateStoryboard(prev => {
                        const elements = [...prev.elements]
                        elements[sbResize.index] = { ...elements[sbResize.index], startTime: newStart, duration: newDur }
                        return { ...prev, elements }
                    })
                }
            }
        }

        const handleUp = () => {
            setSbDrag(null)
            setSbResize(null)
        }

        window.addEventListener('pointermove', handleMove)
        window.addEventListener('pointerup', handleUp)
        return () => {
            window.removeEventListener('pointermove', handleMove)
            window.removeEventListener('pointerup', handleUp)
        }
    }, [sbDrag, sbResize, pixelsToMs, updateStoryboard, snapTime, storyboard, scriptTimings, snapEnabled])

    // ==============================
    // AUDIO ENGINE — preload clips via Web Audio API
    // ==============================
    useEffect(() => {
        const clips = scripts
            .filter(s => s.audio_url && s._id)
            .map(s => ({ id: s._id, url: s.audio_url }))
        if (clips.length > 0) {
            getAudioEngine().preload(clips).then(() => {
                // Trigger re-computation of script timings now that we have
                // actual decoded audio buffer durations (most accurate source)
                setBufferLoadCount(c => c + 1)
            })
        }
    }, [scripts, getAudioEngine])

    const getScriptAtTime = useCallback((time) => {
        for (let i = 0; i < scripts.length; i++) {
            const t = scriptTimings[i]
            if (time >= t.start && time < t.start + t.duration) {
                return { script: scripts[i], timing: t, offset: time - t.start }
            }
        }
        return null
    }, [scripts, scriptTimings])

    // ==============================
    // PLAYBACK
    // ==============================
    const startPlayback = useCallback(() => {
        if (playbackIntervalRef.current) return
        setIsPlaying(true)
        playbackRef.current = true
        playbackLastTickRef.current = performance.now()

        const engine = getAudioEngine()
        engine.resume() // must be inside a user-gesture handler

        const hit = getScriptAtTime(playheadTime)
        if (hit && hit.script.audio_url) {
            engine.play(hit.script._id, hit.offset)
        } else {
            engine.stop()
        }

        playbackIntervalRef.current = setInterval(() => {
            const now = performance.now()
            const delta = now - playbackLastTickRef.current
            playbackLastTickRef.current = now

            setPlayheadTime(prev => {
                const next = prev + delta
                if (next >= totalDuration) {
                    clearInterval(playbackIntervalRef.current)
                    playbackIntervalRef.current = null
                    playbackRef.current = false
                    setIsPlaying(false)
                    engine.stop()
                    return totalDuration
                }
                return next
            })
        }, 50)
    }, [totalDuration, playheadTime, getScriptAtTime, getAudioEngine])

    const pausePlayback = useCallback(() => {
        if (playbackIntervalRef.current) {
            clearInterval(playbackIntervalRef.current)
            playbackIntervalRef.current = null
        }
        playbackRef.current = false
        setIsPlaying(false)
        getAudioEngine().stop()
    }, [getAudioEngine])

    // During playback: audio transitions & active element tracking
    useEffect(() => {
        const engine = getAudioEngine()

        // When playback is stopped, ensure audio is fully stopped.
        // This guards against a race condition where a queued playheadTime
        // update (from the interval) triggers this effect after pausePlayback
        // already called engine.stop() — without this, the effect could
        // restart audio because currentClipId was cleared by stop().
        if (!isPlaying) {
            engine.stop()
            return
        }

        const hit = getScriptAtTime(playheadTime)

        if (hit && hit.script.audio_url) {
            engine.syncPlayback(hit.script._id, hit.offset)
        } else {
            engine.syncPlayback(null)
        }

        const sbIdx = getActiveElementAtTime(playheadTime)
        if (sbIdx !== selectedElementIndex) {
            setSelectedElementIndex(sbIdx)
            setSelectedElementIndices(sbIdx !== null ? new Set([sbIdx]) : new Set())
            triggerDeselectItems()
        }
    }, [playheadTime, isPlaying, getScriptAtTime, getAudioEngine, getActiveElementAtTime, selectedElementIndex, setSelectedElementIndex, triggerDeselectItems])

    // Auto-scroll timeline to keep playhead visible during playback
    useEffect(() => {
        if (!isPlaying) return
        const container = timelineScrollRef.current
        if (!container) return
        const playheadPx = (playheadTime / 1000) * scale
        const viewLeft = container.scrollLeft
        const viewRight = viewLeft + container.clientWidth
        const margin = 50
        if (playheadPx > viewRight - margin) {
            container.scrollLeft = playheadPx - container.clientWidth + margin
        } else if (playheadPx < viewLeft + margin) {
            container.scrollLeft = playheadPx - margin
        }
    }, [playheadTime, isPlaying, scale])

    const handlePlay = () => startPlayback()
    const handleStop = () => pausePlayback()

    const seekTo = useCallback((timeMs) => {
        setPlayheadTime(timeMs)
        if (isPlaying) {
            const engine = getAudioEngine()
            const hit = getScriptAtTime(timeMs)
            if (hit && hit.script.audio_url) {
                engine.play(hit.script._id, hit.offset)
            } else {
                engine.stop()
            }
        }
        const sbIdx = getActiveElementAtTime(timeMs)
        setSelectedElementIndex(sbIdx)
        setSelectedElementIndices(sbIdx !== null ? new Set([sbIdx]) : new Set())
        triggerDeselectItems()
    }, [isPlaying, getScriptAtTime, getAudioEngine, getActiveElementAtTime, setSelectedElementIndex, triggerDeselectItems])

    const handleScrubChange = (e) => {
        const val = parseFloat(e.target.value)
        seekTo(val * totalDuration)
    }

    const handlePrev = () => {
        for (let i = scripts.length - 1; i >= 0; i--) {
            if (scriptTimings[i].start < playheadTime - 200) {
                seekTo(scriptTimings[i].start)
                return
            }
        }
        seekTo(0)
    }

    const handleNext = () => {
        for (let i = 0; i < scripts.length; i++) {
            if (scriptTimings[i].start > playheadTime + 200) {
                seekTo(scriptTimings[i].start)
                return
            }
        }
    }

    // --- Script element click ---
    const handleScriptClick = (scriptElement) => {
        openModal(<ScriptEditorModal script={scriptElement} />, 'side')
    }

    // --- Generate voices ---
    const generateVoices = async (skipExisting) => {
        const items = scripts.filter(s => s.character)
        const toGenerate = skipExisting ? items.filter(s => !s.audio_url) : items
        if (toGenerate.length === 0) return

        const confirmed = await openModal(
            <ConfirmModal
                title={skipExisting ? 'Generate Missing Voices' : 'Regenerate All Voices'}
                message={skipExisting
                    ? `Generate voices for ${toGenerate.length} script element${toGenerate.length === 1 ? '' : 's'} with missing audio?`
                    : `Regenerate voices for all ${toGenerate.length} script element${toGenerate.length === 1 ? '' : 's'}? This will replace existing audio.`
                }
            />
        )
        if (!confirmed) return

        try {
            setGeneratingVoices(true)
            let counter = 0

            for (const item of toGenerate) {
                counter++
                setVoiceGenProgress(`Generating voice ${counter}/${toGenerate.length}...`)
                await runTaskAndPoll({
                    function: 'generate-voice',
                    showId: segment?.show,
                    payload: { scriptId: item._id }
                })
            }

            await fetchEpisode()
        } catch (error) {
            console.error('Error generating voices:', error)
        } finally {
            setGeneratingVoices(false)
            setVoiceGenProgress('')
        }
    }

    // --- Cleanup ---
    useEffect(() => {
        return () => {
            if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current)
            if (audioEngineRef.current) audioEngineRef.current.destroy()
            playbackRef.current = false
        }
    }, [])

    // --- Keyboard shortcuts ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Don't intercept when typing in a text input/textarea/contenteditable
            const el = document.activeElement
            if (el?.tagName === 'TEXTAREA' || el?.tagName === 'SELECT') return
            if (el?.tagName === 'INPUT' && el.type !== 'range') return
            if (el?.isContentEditable) return

            // Space toggles playback globally
            if (e.key === ' ') {
                e.preventDefault()
                if (isPlaying) pausePlayback()
                else startPlayback()
                return
            }

            // Delete only when pointer is over the timeline
            if (!pointerOverTimelineRef.current) return
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault()
                deleteSelectedElements()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [deleteSelectedElements, isPlaying, pausePlayback, startPlayback])

    // ==============================
    // RENDER
    // ==============================
    return (
        <div
            onPointerEnter={() => { pointerOverTimelineRef.current = true }}
            onPointerLeave={() => { pointerOverTimelineRef.current = false }}
        >
            {/* ======== PLAYBACK CONTROLS ======== */}
            <div className='flex items-center justify-between px-4 py-2 border-b'>
                <div className='flex items-center'>
                    <Button type={snapEnabled ? 'primary' : 'secondary'}  icon={<TbMagnet className='w-3 h-3' />} onClick={() => setSnapEnabled(s => !s)} title={snapEnabled ? 'Snapping on' : 'Snapping off'}/>
                   
                </div>
                
                
                <div className='flex items-center gap-2'>
                    <div className='flex items-center'>
                        <Button type='secondary' className='rounded-r-none' title='Previous panel' icon={<IoMdSkipBackward className='w-4 h-4' />} onClick={handlePrev} />
                        {isPlaying
                            ? <Button type='secondary' className='rounded-none border-l-0' title='Pause' icon={<IoMdPause className='w-4 h-4' />} onClick={handleStop} />
                            : <Button type='secondary' className='rounded-none border-l-0' title='Play' icon={<IoMdPlay className='w-4 h-4' />} onClick={handlePlay} />
                        }
                        <Button type='secondary' className='rounded-l-none border-l-0' title='Next panel' icon={<IoMdSkipForward className='w-4 h-4' />} onClick={handleNext} />
                    </div>
                    <span className='text-md font-mono text-secondary-foreground/60 whitespace-nowrap'>{`${formatTime(playheadTime)} / ${formatTime(totalDuration)}`}</span>
                </div>
                
                <div className='flex items-center overflow-hidden rounded-md'>
                    <Button type='secondary' className='rounded-none' title='Zoom in' icon={<FiZoomIn className='w-3 h-3' />} onClick={() => zoomTimeline(scale + 10)} />
                    <Button type='secondary' className='rounded-none' title='Zoom out' icon={<FiZoomOut className='w-3 h-3' />} onClick={() => zoomTimeline(scale - 10)} />
                </div>
            </div>

            {/* ======== TIMELINE ======== */}
            <div className='flex flex-col flex-none'>
                <div className='flex'>
                    {/* Sidebar labels */}
                    <div className='flex flex-col min-w-36 border-r bg-card'>
                        <div className='h-5 border-b' /> {/* spacer for time ruler */}
                        <div className='flex items-center justify-between h-16 px-3 border-b border-b-secondary-foreground/10'>
                            <span className='text-[10px] text-secondary-foreground'>Storyboard</span>
                            <Button type='secondary' size='xs' title='Add storyboard element' icon={<FiPlus className='w-3 h-3' />} onClick={addStoryboardElement} />
                        </div>
                        <div className='flex items-center justify-between h-20 px-3 gap-1'>
                            <div className='flex flex-col gap-0.5 min-w-0'>
                                <span className='text-[10px] text-secondary-foreground'>Script</span>
                                {voiceGenProgress && (
                                    <span className='text-[9px] text-primary truncate'>{voiceGenProgress}</span>
                                )}
                            </div>
                            <div className='flex flex-none'>
                                <Button
                                    type='secondary'
                                    size='xs'
                                    className='rounded-r-none'
                                    icon={<LuAudioLines className='w-3 h-3' />}
                                    disabled={generatingVoices || scripts.filter(s => s.character && !s.audio_url).length === 0}
                                    onClick={() => generateVoices(true)}
                                    title='Generate missing voices'
                                />
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <ShadButton
                                            variant='secondary'
                                            disabled={generatingVoices || scripts.filter(s => s.character).length === 0}
                                            className='rounded-l-none border-l-0 border bg-secondary h-full px-0.5 py-0.5 min-w-0'
                                        >
                                            <FiChevronDown className='w-3 h-3' />
                                        </ShadButton>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        <DropdownMenuItem className='p-1 text-xs' onClick={() => generateVoices(false)}>
                                            Regenerate all voices
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    </div>

                    {/* Timeline content */}
                    <div ref={timelineScrollRef} className='flex flex-col flex-1 overflow-x-auto overflow-y-hidden'>
                        <div ref={timelineRef} className='relative' style={{ width: `${timelineWidth}px`, minWidth: '100%' }}>
                            {/* Time ruler / scrub */}
                            <div className='flex h-5 relative sticky top-0 z-30 bg-card border-b' style={{ width: `${timelineWidth}px` }}>
                                <div className='flex text-[9px] text-secondary-foreground/50 absolute top-0 left-0 h-full pointer-events-none overflow-hidden' style={{ width: `${timelineWidth}px` }}>
                                    {Array.from({ length: Math.ceil(totalDuration / 5000) }).map((_, i) => (
                                        <span key={i} className='flex flex-none items-center px-1' style={{ width: `${5 * scale}px` }}>
                                            {formatTime(i * 5000)}
                                        </span>
                                    ))}
                                </div>
                                <input
                                    ref={scrubRef}
                                    type='range'
                                    min={0}
                                    max={1}
                                    step={0.0001}
                                    className='z-10 timeline-range'
                                    style={{ width: `${timelineWidth}px` }}
                                    value={scrubPosition}
                                    onChange={handleScrubChange}
                                />
                            </div>

                            {/* Playhead */}
                            <div
                                className='absolute top-0 w-[1px] border-r border-r-primary/60 h-full pointer-events-none z-20'
                                style={{ left: `${msToPixels(playheadTime)}px` }}
                            />

                            {/* Track 1: Storyboard */}
                            <div className='flex items-center h-16 border-b relative' style={{ width: `${timelineWidth}px` }} onClick={() => setSelectedElementIndices(new Set())}>
                                {storyboard?.elements?.map((el, index) => {
                                    const leftPx = msToPixels(el.startTime)
                                    const widthPx = msToPixels(el.duration)
                                    const isSelected = selectedElementIndices.has(index)
                                    const isSingleSelected = isSelected && selectedElementIndices.size === 1
                                    return (
                                        <div
                                            key={el._id || index}
                                            className={`absolute flex items-start border rounded-md h-12 overflow-hidden cursor-grab group ${isSelected ? 'border-primary bg-primary/20' : 'bg-secondary/40'}`}
                                            style={{left: `${leftPx}px`, width: `${widthPx}px`, top: '8px'}}
                                            onClick={(e) => e.stopPropagation()}
                                            onPointerDown={(e) => handleSbDragStart(e, index)}
                                        >
                                            {isSingleSelected && (
                                                <div
                                                    className={`absolute left-0 top-0 w-1.5 h-full cursor-ew-resize z-10 hover:bg-primary/40 ${sbResize?.index === index && sbResize?.edge === 'left' ? 'bg-primary/40' : 'bg-primary/0'}`}
                                                    onPointerDown={(e) => handleSbResizeStart(e, index, 'left')}
                                                />
                                            )}
                                            <div className='flex items-center gap-1 px-2 py-1 flex-1 overflow-hidden'>
                                                <FiImage className='w-3 h-3 text-secondary-foreground flex-none' />
                                                <span className='text-[10px] text-secondary-foreground truncate'>{el.name}</span>
                                            </div>
                                            {isSingleSelected && (
                                                <div
                                                    className={`absolute right-0 top-0 w-1.5 h-full cursor-ew-resize z-10 hover:bg-primary/40 ${sbResize?.index === index && sbResize?.edge === 'right' ? 'bg-primary/40' : 'bg-primary/0'}`}
                                                    onPointerDown={(e) => handleSbResizeStart(e, index, 'right')}
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Track 2: Script */}
                            <div className='flex items-center h-20 border-b relative' style={{ width: `${timelineWidth}px` }}>
                                {scripts.map((s, index) => {
                                    const timing = scriptTimings[index]
                                    const leftPx = msToPixels(timing.start)
                                    const widthPx = msToPixels(timing.duration)
                                    const charColorIndex = uniqueCharacters.findIndex(c => c._id === s.character?._id) + 1
                                    const isActiveScript = activeScriptIndex === index
                                    return (
                                        <div
                                            key={s._id || index}
                                            className={`absolute flex flex-col border rounded-md py-1 px-2 h-16 overflow-hidden cursor-pointer hover:brightness-110 group/script cursor-grab active:cursor-grabbing ${isActiveScript ? 'ring-1 ring-amber-400/80 brightness-125 z-10' : ''}`}
                                            onClick={() => handleScriptClick(s)}
                                            draggable={selectedElementIndex !== null}
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData(SCRIPT_DRAG_TYPE, JSON.stringify({ _id: s._id }))
                                                e.dataTransfer.effectAllowed = 'copy'
                                                e.stopPropagation()
                                            }}
                                            style={{
                                                left: `${leftPx}px`,
                                                width: `${widthPx}px`,
                                                top: '8px',
                                                backgroundColor: `var(--timeline-${charColorIndex || 1})`,
                                            }}
                                        >
                                            <div className='flex items-center justify-between text-[10px] text-secondary-foreground/80'>
                                                <div className='flex items-center gap-1'>
                                                    <span>{s.character?.name}</span>
                                                    {(s.speech !== s.tts_speech || s.character?._id !== s.tts_character?._id) && s.audio_url && <FiAlertTriangle className='w-3 h-3 text-orange-400' />}
                                                </div>
                                                <div className='flex items-center gap-1'>
                                                    {s.audio_duration && (
                                                        <span>{(s.audio_duration / 1000).toFixed(1)}s</span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <span className='text-[10px] text-secondary-foreground/60 overflow-hidden flex-1 line-clamp-1'>
                                                {s.speech || ''}
                                            </span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
